import { ChangeDetectionStrategy, Component, computed, inject, input, output, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { Avatar } from '../avatar/avatar';
import { ZagIcon } from '../icon/icon';
import { PersonCardData } from '../person-card/person-card';

/**
 * PersonListRow Component
 *
 * Displays a person's information in a horizontal row format for list views.
 * Shows avatar, full name, role, and an edit action.
 *
 * @example
 * ```html
 * <zag-person-list-row
 *   [person]="personData"
 *   [editRoute]="['/persons', personData.id, 'edit']"
 *   (rowClick)="onPersonSelected($event)">
 * </zag-person-list-row>
 * ```
 */
@Component({
  selector: 'zag-person-list-row',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [Avatar, ZagIcon],
  host: {
    '[class.zag-person-list-row]': 'true',
    '[attr.role]': '"button"',
    '[attr.tabindex]': '0',
    '(click)': 'onRowClick()',
    '(keydown)': 'onRowKeydown($event)',
  },
  template: `
    <zag-avatar [name]="fullName()" [src]="person().profilePicture" size="md" class="zag-person-list-row__avatar" />
    <div class="zag-person-list-row__info">
      <span class="zag-person-list-row__name">{{ fullName() }}</span>
      @if (subtitle()) {
        <span class="zag-person-list-row__subtitle">{{ subtitle() }}</span>
      }
    </div>
    @if (showDelete()) {
      <button type="button" class="zag-person-list-row__delete-btn" [attr.aria-label]="deleteAriaLabel()" (click)="onDeleteClick($event)">
        <span zagIcon="delete"></span>
      </button>
    }
  `,
  styles: [
    `
      .zag-person-list-row {
        display: flex;
        align-items: center;
        padding: var(--spacing-sm) var(--spacing-md);
        gap: var(--spacing-md);
        cursor: pointer;
        transition: box-shadow 0.15s ease;
        background-color: var(--surface-color-default);
        border: 1px solid var(--border-color-default);
        border-radius: var(--border-radius-md);

        &:focus-visible {
          outline: 2px solid var(--color-primary);
          outline-offset: 2px;
        }
      }

      .zag-person-list-row__avatar {
        flex-shrink: 0;
      }

      .zag-person-list-row__info {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
      }

      .zag-person-list-row__name {
        font-weight: 500;
        color: var(--text-color-primary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .zag-person-list-row__subtitle {
        font-size: var(--font-size-sm);
        color: var(--text-color-secondary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .zag-person-list-row__edit {
        flex-shrink: 0;
        padding: var(--spacing-xs);
        color: var(--text-color-secondary);
        border-radius: var(--border-radius-sm);
        display: flex;
        align-items: center;
        justify-content: center;
        text-decoration: none;
        transition:
          background-color 0.15s,
          color 0.15s;

        &:hover {
          background-color: var(--color-info-light);
          color: var(--color-primary);
        }

        [zagIcon] {
          font-size: 18px;
        }
      }

      .zag-person-list-row__delete-btn {
        flex-shrink: 0;
        padding: var(--spacing-xs);
        color: var(--text-color-secondary);
        border: none;
        background: none;
        border-radius: var(--border-radius-sm);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition:
          background-color 0.15s,
          color 0.15s;

        &:hover:not(:disabled) {
          background-color: var(--color-error-light);
          color: var(--color-error);
        }

        [zagIcon] {
          font-size: 18px;
        }
      }
    `,
  ],
})
export class PersonListRow {
  private readonly router = inject(Router);

  /** The person data to display */
  readonly person = input.required<PersonCardData>();

  /** Route for the edit link (array format for routerLink) */
  readonly editRoute = input<string[]>();

  /** Accessible label for the edit action */
  readonly editAriaLabel = input('');

  /** Whether to show the delete button */
  readonly showDelete = input(false);

  /** Accessible label for the delete button */
  readonly deleteAriaLabel = input('');

  /** Emitted when the row is clicked */
  readonly rowClick = output<PersonCardData>();

  /** Emitted when the delete button is clicked */
  readonly deleteClick = output<PersonCardData>();

  /** Computed full name */
  readonly fullName = computed(() => {
    const p = this.person();
    return `${p.firstName} ${p.lastName}`;
  });

  /** Computed subtitle: employee number and role joined by comma */
  readonly subtitle = computed(() => {
    const p = this.person();
    return [p.employeeNumber, p.role].filter(Boolean).join(', ');
  });

  /** Handle row click - navigates to editRoute if set, then emits rowClick */
  onRowClick(): void {
    const route = this.editRoute();
    if (route) {
      this.router.navigate(route);
    }
    this.rowClick.emit(this.person());
  }

  /** Handle keyboard activation for accessibility */
  onRowKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('a') || target?.closest('button')) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onRowClick();
    }
  }

  /** Handle delete button click - stops propagation and emits deleteClick */
  onDeleteClick(event: Event): void {
    event.stopPropagation();
    this.deleteClick.emit(this.person());
  }
}
